import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplyIcon from '@mui/icons-material/Reply';
import { format, parseISO } from 'date-fns';
import { getLiveThread, LiveMessageSummary } from '../api/mailbox';
import LiveMessagePage from './LiveMessagePage';
import MessageContextMenu, {
  MessageActionTarget,
  setMessageDragData,
} from '../components/MessageContextMenu';
import { decodeFolderKey, decodeThreadKey } from '../utils/imapPaths';

export default function LiveThreadPage() {
  const { accountId, folderKey, threadKey } = useParams<{
    accountId: string;
    folderKey: string;
    threadKey: string;
  }>();
  const account = parseInt(accountId!, 10);
  const folder = decodeFolderKey(folderKey!);
  const threadId = decodeThreadKey(threadKey!);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preferredUid = parseInt(searchParams.get('uid') || '', 10);
  const uids = searchParams.get('uids') || undefined;
  const [selectedUid, setSelectedUid] = useState<number | null>(
    Number.isFinite(preferredUid) ? preferredUid : null
  );
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [menuMessage, setMenuMessage] = useState<MessageActionTarget | null>(null);

  const { data: thread, isLoading, isError } = useQuery({
    queryKey: ['live-thread', account, folder, threadId, uids],
    queryFn: () =>
      getLiveThread({ account, folder, thread_id: threadId, uids }),
    enabled: Boolean(account && folder && threadId),
  });

  const messages = (thread?.messages || [])
    .slice()
    .sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return da - db;
    });

  useEffect(() => {
    if (!messages.length) return;
    if (selectedUid && messages.some((m) => m.uid === selectedUid)) return;
    if (Number.isFinite(preferredUid) && messages.some((m) => m.uid === preferredUid)) {
      setSelectedUid(preferredUid);
      return;
    }
    setSelectedUid(messages[messages.length - 1].uid);
  }, [messages, preferredUid, selectedUid]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError || !thread) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load conversation from IMAP.
      </Alert>
    );
  }

  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), 'MMM d, HH:mm');
    } catch {
      return dateStr;
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
      <Box
        sx={{
          width: { xs: '100%', md: 340 },
          borderRight: { md: 1 },
          borderBottom: { xs: 1, md: 0 },
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: { xs: '40%', md: '100%' },
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.5 }}>
          <Tooltip title="Back">
            <IconButton onClick={() => navigate(-1)} aria-label="Back">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" color="primary.main" sx={{ lineHeight: 1 }}>
              Conversation
            </Typography>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {thread.subject || '(no subject)'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {thread.message_count} message{thread.message_count === 1 ? '' : 's'} (live IMAP)
            </Typography>
          </Box>
        </Stack>
        <Divider />
        <List dense disablePadding sx={{ overflow: 'auto', flexGrow: 1 }}>
          {messages.map((msg: LiveMessageSummary) => {
            const target: MessageActionTarget = {
              account,
              imap_folder: folder,
              uid: msg.uid,
              from_address: msg.from_address,
              subject: msg.subject,
            };
            return (
              <ListItemButton
                key={msg.uid}
                selected={msg.uid === selectedUid}
                draggable
                onDragStart={(e) => setMessageDragData(e, target)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setMenuMessage(target);
                  setMenuPos({ top: e.clientY, left: e.clientX });
                }}
                onClick={() => setSelectedUid(msg.uid)}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={msg.is_read ? 400 : 700} noWrap>
                      {msg.from_address || '(unknown)'}
                    </Typography>
                  }
                  secondary={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                        {msg.date ? formatDate(msg.date) : ''}
                      </Typography>
                      {msg.is_starred && <Chip size="small" label="★" sx={{ height: 18 }} />}
                    </Stack>
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
        <Divider />
        <Box sx={{ p: 1 }}>
          <Tooltip title="Reply to latest">
            <IconButton
              onClick={() => {
                const last = messages[messages.length - 1];
                if (!last) return;
                navigate(
                  `/compose?replyAccount=${account}&replyFolder=${encodeURIComponent(folder)}&replyUid=${last.uid}`
                );
              }}
            >
              <ReplyIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {selectedUid ? (
          <LiveMessagePage
            accountId={account}
            imapFolder={folder}
            uid={selectedUid}
            embedded
            hideBack
          />
        ) : (
          <Paper sx={{ m: 2, p: 3 }}>
            <Typography color="text.secondary">Select a message in the thread.</Typography>
          </Paper>
        )}
      </Box>

      <MessageContextMenu
        anchorPosition={menuPos}
        message={menuMessage}
        onClose={() => {
          setMenuPos(null);
          setMenuMessage(null);
        }}
        afterLeave={() => navigate(-1)}
      />
    </Box>
  );
}
